import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MenuButton } from "../MenuButton/MenuButton";
import { MenuItem } from "primereact/menuitem";
import { errorToast } from "@idwal/idwal-react-components";
import { DefectSummaryDataItem } from "../../types/DefectSummaryDataItem";
import {
  SharedReportAsset,
  SharedReportDocuments,
} from "../../types/SharedReportDocuments";
import { getSharedReportDocuments } from "../../services/SharedDefectsService";
import { downloadFile } from "../../utils/AssetHelper";
import { sendGAEvent } from "../../AnalyticsService";
import { ReportTermsDialog } from "./ReportTermsDialog";
import {
  DEFECTS_SUMMARY,
  DEFECTS_SUMMARY_DOWNLOAD_IDWAL_REPORT_CLICK,
  DEFECTS_SUMMARY_DOWNLOAD_SUPPORTING_DOCUMENTS_CLICK,
  DEFECTS_SUMMARY_DOWNLOAD_TECHNICAL_REPORT_CLICK,
} from "../../constants/analytics";

type SharedDefectSummaryActionMenuProps = {
  rowData: DefectSummaryDataItem;
};

type PendingDownload = {
  url: string;
  fileName: string;
  gaAction: string;
};

export const SharedDefectSummaryActionMenu = (
  props: SharedDefectSummaryActionMenuProps
) => {
  const { rowData } = props;
  const { t } = useTranslation("locale");

  const [documents, setDocuments] = useState<SharedReportDocuments | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [pendingDownload, setPendingDownload] =
    useState<PendingDownload | null>(null);

  const inspectionId = rowData.reportSharedInspectionGuid;

  if (!inspectionId) {
    return null;
  }

  // Documents are fetched on menu open rather than on mount, so a page of vessel
  // rows doesn't fan out a presigned-url request per row. Every open re-fetches
  // (previous items stay visible meanwhile) because the presigned urls expire.
  const loadDocuments = () => {
    if (loading) {
      return;
    }

    setLoading(true);
    setFetchFailed(false);

    getSharedReportDocuments(inspectionId)
      .then(setDocuments)
      .catch((error) => {
        console.error(error);
        // Drop any previous result too: its presigned urls may have expired
        setDocuments(null);
        setFetchFailed(true);
        errorToast(t("defectsSummary.documentsError"));
      })
      .finally(() => setLoading(false));
  };

  const startDownload = (download: PendingDownload) => {
    sendGAEvent(DEFECTS_SUMMARY, download.gaAction);
    downloadFile(download.url, download.fileName);
  };

  const downloadItem = (
    label: string,
    gaAction: string,
    requiresTerms: boolean,
    asset?: SharedReportAsset | null,
    fileName?: string | null
  ): MenuItem[] => {
    const url = asset?.value?.url;

    if (!url) {
      return [];
    }

    return [
      {
        label,
        icon: "pi pi-download",
        // Items stay visible but disabled during a re-fetch so a stale
        // presigned url can never be followed.
        disabled: loading,
        command: () => {
          const download = {
            url,
            fileName: fileName ?? asset!.value!.fileName,
            gaAction,
          };

          // Report downloads need a terms acknowledgement first; the GA event
          // fires on agreement so the counts stay true download counts.
          if (requiresTerms) {
            setPendingDownload(download);
            return;
          }

          startDownload(download);
        },
      },
    ];
  };

  const agreeAndDownload = () => {
    if (pendingDownload) {
      startDownload(pendingDownload);
    }

    setPendingDownload(null);
  };

  let menuItems: MenuItem[];

  if (!documents && fetchFailed && !loading) {
    menuItems = [
      { label: t("defectsSummary.documentsLoadFailed"), disabled: true },
    ];
  } else if (!documents) {
    menuItems = [
      { label: t("defectsSummary.loadingDocuments"), disabled: true },
    ];
  } else {
    menuItems = [
      ...downloadItem(
        t("defectsSummary.idwalReport"),
        DEFECTS_SUMMARY_DOWNLOAD_IDWAL_REPORT_CLICK,
        true,
        documents.fullReport
      ),
      ...downloadItem(
        t("defectsSummary.supportingDocuments"),
        DEFECTS_SUMMARY_DOWNLOAD_SUPPORTING_DOCUMENTS_CLICK,
        false,
        documents.zipElement,
        documents.zipFileName
      ),
      ...downloadItem(
        t("defectsSummary.technicalReport"),
        DEFECTS_SUMMARY_DOWNLOAD_TECHNICAL_REPORT_CLICK,
        true,
        documents.technicalReportElement
      ),
    ];

    if (!menuItems.length) {
      menuItems = [
        { label: t("defectsSummary.noDocumentsAvailable"), disabled: true },
      ];
    }
  }

  return (
    <div className="flex align-items-center justify-content-center">
      <MenuButton
        label={t("defectsSummary.download")}
        onOpen={loadDocuments}
        menu={menuItems}
      />
      <ReportTermsDialog
        visible={!!pendingDownload}
        customerName={documents?.customerName}
        customerOrganisation={documents?.customerOrganisation}
        onAgree={agreeAndDownload}
        onHide={() => setPendingDownload(null)}
      />
    </div>
  );
};
